// src/pages/User/CVSAForm.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import "../../styles/CVSAForm.css";
import logo from "../../images/logo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const CVSA_TASK_ID = 36;

const CVSAForm = () => {
  const navigate = useNavigate();
  const { id: paramId, cvsaId } = useParams();
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const isNew = !cvsaId || cvsaId === "new";

  const [form, setForm] = useState({
    inspection_number: "",
    administrator: "",
    terminal: "",
    inspection_level: "",
    inspection_date: "",
    inspection_time: "",
    location: "",
    agency: "",

    employee_id: "",
    employee_name: "",
    driver_result: "",
    driver_description: "",
    employee_corrective_date: "",
    employee_corrective_actions: "",

    truck_number: "",
    truck_description: "",
    truck_result: "",
    truck_required_actions: "",
    truck_corrective_actions: "",

    trailer_unit: "",
    trailer_description: "",
    trailer_result: "",
    trailer_required_actions: "",
    trailer_corrective_actions: "",

    notes: "",
  });

  const [readyForReview, setReadyForReview] = useState(false);
  const [file, setFile] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [uploadedOriginalName, setUploadedOriginalName] = useState(null);

  // Load existing inspection + employee info
  useEffect(() => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    // 🔹 Fetch employee info
    const fetchEmployee = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/cvsa/employee/${userId}`,
          { headers }
        );
        setForm((prev) => ({
          ...prev,
          employee_id: data.employee_id,
          employee_name: data.employee_name,
        }));
      } catch (err) {
        console.error("❌ Error fetching employee info", err);
      }
    };

    const fetchInspection = async () => {
      if (!isNew) {
        try {
          const { data } = await axios.get(
            `${API_BASE}/api/cvsa/detail/${cvsaId}`,
            { headers }
          );

          setForm((prev) => ({
            ...prev,
            ...data,
            inspection_date: data.inspection_date
              ? new Date(data.inspection_date).toISOString().split("T")[0]
              : "",
            employee_corrective_date: data.employee_corrective_date
              ? new Date(data.employee_corrective_date)
                  .toISOString()
                  .split("T")[0]
              : "",
          }));

          setReadyForReview(!!data.cvsa_ready_for_review);

          // ✅ Capture uploaded file info
          if (data.cvsa_file) {
            setUploadedFileName(data.cvsa_file);
            setUploadedOriginalName(data.cvsa_original || data.cvsa_file);
          }
        } catch (err) {
          console.error("❌ Error loading CVSA record", err);
        }
      }
    };

    fetchEmployee();
    fetchInspection();
  }, [isNew, cvsaId, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, value ?? "");
    });
    if (file) formData.append("file", file);
    formData.append("userId", userId);

    try {
      if (isNew) {
        await axios.post(`${API_BASE}/api/cvsa?userId=${userId}`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        alert("✔️ Inspection created");
      } else {
        await axios.put(
          `${API_BASE}/api/cvsa/${cvsaId}?userId=${userId}`,
          formData,
          {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
          }
        );
        alert("✔️ Inspection updated");
      }
    } catch (err) {
      console.error("❌ Save error", err);
      alert("Save failed");
    }
  };

  const handleReviewToggle = async (e) => {
    const checked = e.target.checked;
    if (checked) {
      if (!window.confirm("Mark this inspection as Ready for Review?")) return;
      try {
        await axios.put(
          `${API_BASE}/api/cvsa/review/${cvsaId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setReadyForReview(true);
        alert("✔️ Marked Ready for Review");
      } catch {
        alert("❌ Failed to update");
      }
    } else {
      if (!isAdmin) return;
      if (!window.confirm("Reset inspection back to Not Completed?")) return;
      try {
        await axios.put(
          `${API_BASE}/api/cvsa/review/reset/${cvsaId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setReadyForReview(false);
        alert("✔️ Reset to Not Completed");
      } catch {
        alert("❌ Reset failed");
      }
    }
  };

  return (
    <>
      {/* 🔝 Header Section */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            CVSA Inspections
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/cvsa/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="cvsa-form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">CVSA Inspections</h2>
          </div>
          <CompanyAddress />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <p className="cvsa-confidential-text">
            The Information contained in this document is confidential. Parties
            privy to the information are required to treat all information,
            personal or otherwise; contained in this document as confidential.
            The contents of this document may only be used for the purpose for
            which it is intended. Duplication or distribution of any information
            contained may only be done at the explicit consent of New Way
            Transport.
          </p>
          {/* General Information */}
          <h4 className="cvsa-section-title">General Information</h4>
          <div className="row mb-3">
            <div className="col">
              <label>Inspection #</label>
              <input
                name="inspection_number"
                value={form.inspection_number}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Administrator</label>
              <input
                name="administrator"
                value={form.administrator}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Terminal</label>
              <input
                name="terminal"
                value={form.terminal}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Inspection Level</label>
              <input
                name="inspection_level"
                value={form.inspection_level}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col">
              <label>Date</label>
              <input
                type="date"
                name="inspection_date"
                value={form.inspection_date}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Time</label>
              <input
                type="time"
                name="inspection_time"
                value={form.inspection_time}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Location</label>
              <input
                name="location"
                value={form.location}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Agency</label>
              <input
                name="agency"
                value={form.agency}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>

          {/* Employee Info */}
          <h4 className="cvsa-section-title">Employee Information</h4>
          <div className="row mb-3">
            <div className="col">
              <label>Employee ID</label>
              <input
                name="employee_id"
                value={form.employee_id}
                readOnly
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Employee Name</label>
              <input
                name="employee_name"
                value={form.employee_name}
                readOnly
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Driver Result</label>
              <input
                name="driver_result"
                value={form.driver_result}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>
          <div className="mb-3">
            <label>Driver Description</label>
            <textarea
              name="driver_description"
              value={form.driver_description}
              onChange={handleChange}
              className="form-control"
              disabled={!isAdmin && readyForReview}
            />
          </div>
          <div className="row mb-3">
            <div className="col-3">
              <label>Employee Corrective Actions</label>
              <input
                type="date"
                name="employee_corrective_date"
                value={form.employee_corrective_date}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col">
              <textarea
                name="employee_corrective_actions"
                value={form.employee_corrective_actions}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>

          {/* Truck Info */}
          <h4 className="cvsa-section-title">Truck Information</h4>
          <div className="row mb-3">
            <div className="col">
              <label>Truck #</label>
              <input
                name="truck_number"
                value={form.truck_number}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Truck Description</label>
              <input
                name="truck_description"
                value={form.truck_description}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Truck Result</label>
              <input
                name="truck_result"
                value={form.truck_result}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>
          <div className="mb-3">
            <label>Truck Required Actions</label>
            <textarea
              name="truck_required_actions"
              value={form.truck_required_actions}
              onChange={handleChange}
              className="form-control"
              disabled={!isAdmin && readyForReview}
            />
          </div>
          <div className="mb-3">
            <label>Truck Corrective Actions</label>
            <textarea
              name="truck_corrective_actions"
              value={form.truck_corrective_actions}
              onChange={handleChange}
              className="form-control"
              disabled={!isAdmin && readyForReview}
            />
          </div>

          {/* Trailer Info */}
          <h4 className="cvsa-section-title">Trailer Information</h4>
          <div className="row mb-3">
            <div className="col">
              <label>Trailer Unit #</label>
              <input
                name="trailer_unit"
                value={form.trailer_unit}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Trailer Description</label>
              <input
                name="trailer_description"
                value={form.trailer_description}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
            <div className="col">
              <label>Trailer Result</label>
              <input
                name="trailer_result"
                value={form.trailer_result}
                onChange={handleChange}
                className="form-control"
                disabled={!isAdmin && readyForReview}
              />
            </div>
          </div>
          <div className="mb-3">
            <label>Trailer Required Actions</label>
            <textarea
              name="trailer_required_actions"
              value={form.trailer_required_actions}
              onChange={handleChange}
              className="form-control"
              disabled={!isAdmin && readyForReview}
            />
          </div>
          <div className="mb-3">
            <label>Trailer Corrective Actions</label>
            <textarea
              name="trailer_corrective_actions"
              value={form.trailer_corrective_actions}
              onChange={handleChange}
              className="form-control"
              disabled={!isAdmin && readyForReview}
            />
          </div>
          {/* Supporting Document */}
          <h4 className="cvsa-section-title">Supporting Document</h4>
          <div className="mb-3">
            <label className="form-label">
              Choose Supporting File (PDF / JPG / PNG):
            </label>
            <input
              type="file"
              className="form-control"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                const selected = e.target.files[0];
                if (!selected) return;

                const allowedTypes = [
                  "application/pdf",
                  "image/jpeg",
                  "image/png",
                ];

                if (allowedTypes.includes(selected.type)) {
                  setFile(selected);
                  setOriginalFileName(selected.name);
                } else {
                  alert("Only PDF, JPG, or PNG files allowed!");
                  setFile(null);
                  setOriginalFileName(null);
                }
              }}
              disabled={!isAdmin && readyForReview}
            />
          </div>

          {/* ✅ Show previously uploaded file (from DB) */}
          {uploadedFileName && !file && (
            <UploadedAttachmentList
              title="Uploaded File"
              files={[
                {
                  fileName: uploadedOriginalName,
                  filePath: uploadedFileName,
                },
              ]}
              getFileKey={(file) => file.filePath}
              getFileName={(file) => file.fileName}
              getFileViewUrl={(file) => `${API_BASE}/uploads/cvsa/${file.filePath}`}
              getFileDownloadName={(file) => file.fileName}
            />
          )}

          {/* ✅ Show newly selected file before upload */}
          {originalFileName && file && (
            <div className="p-3 bg-light border rounded">
              <div className="text-muted mb-2">Selected File:</div>
              <a
                href={URL.createObjectURL(file)}
                target="_blank"
                rel="noopener noreferrer"
              >
                📄 {originalFileName}
              </a>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-4">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="readyForReview"
                checked={readyForReview}
                onChange={handleReviewToggle}
                disabled={!isAdmin && readyForReview}
              />
              <label className="form-check-label ms-2" htmlFor="readyForReview">
                Ready for Review
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && readyForReview}
            >
              Save
            </button>
          </div>
        </form>

        {/* Review Manager for admin */}

        {!isNew && (
          <ReviewManager
            taskName="cvsa_inspections"
            taskId={CVSA_TASK_ID}
            referenceId={cvsaId}
            userId={userId}
            isAdmin={isAdmin}
          />
        )}
      </div>
    </>
  );
};

export default CVSAForm;


